import _ from 'lodash';
import moment from 'moment';
import {getDate} from 'utils/datetime';

import {
  createReducer,
  createFetchReducers,
  createFetchActionForNamespace,
  createFetchSelectors,
  createPostActionForNamespace
} from 'utils/reducers';
import {transformFiltersForStoreToFiltersForApi} from 'utils/filters';
import {selectFilters, FILTERS} from 'ducks/filters';

/*
 * We want to limit the allocations timeline to be:
 *   - from 1 May 2016
 *   - to today + 5 years
*/
export const TIMELINE_START = getDate('01/05/2016').valueOf();
export const TIMELINE_END = moment()
  .add(5, 'years')
  .valueOf();

const createFetchAction = createFetchActionForNamespace('allocations');
const createPostAction = createPostActionForNamespace('allocations');

export const fetchAllocationStatistics = createFetchAction({
  name: 'FETCH_ALLOCATION_STATISTICS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'allocationStatistics',
  defaultStateValue: [],
  extraGetParamsSelectors: [
    state =>
      transformFiltersForStoreToFiltersForApi(
        selectFilters(state, FILTERS.ALLOCATIONS_CALENDAR)
      )
  ]
});

const fetchAllocationStatisticsSelector = createFetchSelectors(
  fetchAllocationStatistics
);

export const selectAllocationStatistics =
  fetchAllocationStatisticsSelector.data;

export const fetchAllocations = createFetchAction({
  name: 'FETCH_ALLOCATIONS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'allocationsList',
  defaultStateValue: [],
  extraGetParamsSelectors: [
    state =>
      transformFiltersForStoreToFiltersForApi(
        selectFilters(state, FILTERS.ALLOCATIONS_CALENDAR)
      )
  ]
});

const fetchAllocationsSelector = createFetchSelectors(fetchAllocations);

export const selectAllocations = fetchAllocationsSelector.data;
export const selectAllocationsLoading = fetchAllocationsSelector.isLoading;

export const MODALS = {
  CREATE_ROOM: 'createRoom',
  ARCHIVE_ROOM: 'archiveRoom',
  UNDO_ARCHIVE_ROOM: 'undoArchiveRoom',
  CREATE_OFFLINE_PERIOD: 'createOfflinePeriod',
  OFFLINE_PERIOD_DETAIL: 'offlinePeriodDetail',
  OUT_OF_SERVICE_REASON: 'outOfServiceReason',
  ASSIGN_ROOM_PURPOSE: 'assignRoomPurpose',
  CONFIRM_ASSIGN_ROOM_PURPOSE: 'confirmAssignRoomPurpose'
};

export const prepareTimelineData = allocations => {
  let groups: Array<any> = [];
  let items: Array<any> = [];

  allocations.forEach(allocation => {
    groups.push({
      id: allocation.roomId,
      title: allocation.label,
      rightTitle: allocation.roomType.name,
      roomId: allocation.roomId,
      cleaningStatus: allocation.cleaningStatus,
      dummy: allocation.dummy,
      purpose: allocation.purpose
    });

    allocation.statuses.forEach(status => {
      const startDate = moment(status.startDate);
      let endDate: any = moment(status.endDate);

      if (_.isNil(status.endDate)) {
        endDate = TIMELINE_END;
      }

      items.push({
        id: status.id,
        group: allocation.roomId,
        startDate,
        endDate,
        bookingType: status.bookingType,
        status: status.status,
        tenants: status.tenants,
        bookingId: status.bookingId,
        offlinePeriodReason: status.offlinePeriodReason,
        offlinePeriodNotes: status.offlinePeriodNotes
      });
    });
  });

  return {groups, items};
};

const SELECT_ROOM_CHECKBOX = 'allocations/SELECT_ROOM_CHECKBOX';
export const selectRoomCheckbox = ({roomId, isSelected}) => ({
  type: SELECT_ROOM_CHECKBOX,
  payload: {
    roomId,
    isSelected
  }
});

export const prepareRoomPurposesTimelineData = allocations => {
  let groups: Array<any> = [];
  let items: Array<any> = [];

  allocations.forEach(allocation => {
    groups.push({
      id: allocation.roomId,
      roomId: allocation.roomId,
      title: allocation.label,
      rightTitle: allocation.roomType.name,
      dummy: allocation.dummy,
      isSelected: false
    });

    allocation.purposes.forEach(purpose => {
      const startDate = moment(purpose.startDate);
      let endDate: any = moment(purpose.endDate);

      if (_.isNil(purpose.endDate)) {
        endDate = TIMELINE_END;
      }

      items.push({
        id: purpose.id,
        group: allocation.roomId,
        startDate,
        endDate,
        purpose: purpose.purpose
      });
    });
  });

  return {groups, items};
};

export const selectTimelineData = state =>
  _.get(state, 'allocations.timelineData', {
    groups: [],
    items: []
  });

export const selectRoomPurposesTimelineData = state =>
  _.get(state, 'allocations.roomPurposesTimelineData', {
    groups: [],
    items: []
  });

export const deleteOfflinePeriod = createPostAction({
  name: 'DELETE_OFFLINE_PERIOD',
  initialAction: (
    {offlinePeriodId, locationRef},
    successExtraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      offlinePeriodId,
      locationRef,
      successExtraActions,
      failureExtraActions
    }
  })
});

export const getChangesBeforeCreatingRoomPurposes = createPostAction({
  name: 'GET_CHANGES_BEFORE_CREATING_ROOM_PURPOSES',
  initialAction: (
    {locationRef, rooms, startDate, endDate, purpose},
    successExtraActions = []
  ) => ({
    payload: {
      locationRef,
      rooms,
      startDate,
      endDate,
      purpose,
      successExtraActions
    }
  })
});

export const fetchOfflinePeriodReasons = createFetchAction({
  name: 'FETCH_OFFLINE_PERIOD_REASONS',
  initialAction: extraActions => ({payload: {extraActions}}),
  stateKey: 'offlinePeriodReasons',
  defaultStateValue: []
});

export const selectOfflinePeriodReasons = createFetchSelectors(
  fetchOfflinePeriodReasons
).data;

export const createOfflinePeriod = createPostAction({
  name: 'CREATE_OFFLINE_PERIOD',
  initialAction: (
    {room, startDate, endDate, reason, locationRef, notes},
    extraActions
  ) => ({
    payload: {
      room,
      startDate,
      endDate,
      reason,
      locationRef,
      notes,
      extraActions
    }
  }),
  formName: 'createOfflinePeriod'
});

export const changeCleaningStatus = createPostAction({
  name: 'CHANGE_CLEANING_STATUS',
  initialAction: (
    {roomId, cleaningStatus, outOfServiceReason, locationRef},
    extraActions = []
  ) => ({
    payload: {
      roomId,
      cleaningStatus,
      outOfServiceReason,
      locationRef,
      extraActions
    }
  }),
  formName: 'addOutOfServiceReason'
});

export const fetchOfflinePeriod = createFetchAction({
  name: 'FETCH_OFFLINE_PERIOD',
  initialAction: ({offlinePeriodId}, extraActions = []) => ({
    payload: {
      offlinePeriodId,
      extraActions
    }
  })
});

export const editOfflinePeriodStartDate = createPostAction({
  name: 'EDIT_OFFLINE_PERIOD_START_DATE',
  initialAction: (
    {offlinePeriodId, newStartDate},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      offlinePeriodId,
      newStartDate,
      extraActions,
      failureExtraActions
    }
  })
});

export const editOfflinePeriodEndDate = createPostAction({
  name: 'EDIT_OFFLINE_PERIOD_END_DATE',
  initialAction: (
    {offlinePeriodId, newEndDate},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      offlinePeriodId,
      newEndDate,
      extraActions,
      failureExtraActions
    }
  })
});

export const editOfflinePeriodReason = createPostAction({
  name: 'EDIT_OFFLINE_PERIOD_REASON',
  initialAction: (
    {offlinePeriodId, newOfflinePeriodReasonId},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      offlinePeriodId,
      newOfflinePeriodReasonId,
      extraActions,
      failureExtraActions
    }
  })
});

export const editOfflinePeriodNotes = createPostAction({
  name: 'EDIT_OFFLINE_PERIOD_NOTES',
  initialAction: (
    {offlinePeriodId, newOfflinePeriodNotes},
    extraActions = [],
    failureExtraActions = []
  ) => ({
    payload: {
      offlinePeriodId,
      newOfflinePeriodNotes,
      extraActions,
      failureExtraActions
    }
  })
});

export const fetchRoomHistory = createFetchAction({
  name: 'FETCH_ROOM_HISTORY',
  initialAction: ({locationRef, roomId}, extraActions) => ({
    payload: {
      locationRef,
      roomId,
      extraActions
    }
  }),
  stateKey: 'roomHistory',
  defaultStateValue: []
});

const roomHistorySelector = createFetchSelectors(fetchRoomHistory);
export const selectRoomHistory = roomHistorySelector.data;
export const selectRoomHistoryLoading = roomHistorySelector.isLoading;

const fetchAllocationsReducer = createFetchReducers(
  fetchAllocations,
  (state, action) => ({
    ...state,
    timelineData: prepareTimelineData(action.payload),
    [action.parent.stateKey]: {
      isLoading: false,
      data: action.payload
    }
  })
);

export const fetchRoomsForRoomPurposes = createFetchAction({
  name: 'FETCH_ROOMS_FOR_ROOM_PURPOSES',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'roomPurposesList',
  defaultStateValue: [],
  extraGetParamsSelectors: [
    state =>
      transformFiltersForStoreToFiltersForApi(
        selectFilters(state, FILTERS.PURPOSES_CALENDAR)
      )
  ]
});

const fetchRoomsForRoomPurposesSelector = createFetchSelectors(
  fetchRoomsForRoomPurposes
);

export const selectRoomsForRoomPurposes =
  fetchRoomsForRoomPurposesSelector.data;
export const selectRoomsForRoomPurposesLoading =
  fetchRoomsForRoomPurposesSelector.isLoading;

const fetchRoomsForRoomPurposesReducer = createFetchReducers(
  fetchRoomsForRoomPurposes,
  (state, action) => ({
    ...state,
    roomPurposesTimelineData: prepareRoomPurposesTimelineData(action.payload),
    [action.parent.stateKey]: {
      isLoading: false,
      data: action.payload
    }
  })
);

export const fetchRoomsForRoomPurposesWithoutFilters = createFetchAction({
  name: 'FETCH_ROOMS_FOR_ROOM_PURPOSES_WITHOUT_FILTERS',
  initialAction: ({locationRef}, extraActions = []) => ({
    payload: {locationRef, extraActions}
  }),
  stateKey: 'allRoomsForAssignPurpose',
  defaultStateValue: []
});

const fetchRoomsForRoomPurposesWithoutFiltersSelector = createFetchSelectors(
  fetchRoomsForRoomPurposesWithoutFilters
);

export const selectRoomsForRoomPurposesWithoutFilters =
  fetchRoomsForRoomPurposesWithoutFiltersSelector.data;

export const assignRoomPurpose = createPostAction({
  name: 'ASSIGN_ROOM_PURPOSE',
  initialAction: (
    {rooms, startDate, endDate, purpose, locationRef},
    extraActions = []
  ) => ({
    payload: {
      rooms,
      startDate,
      endDate,
      purpose,
      locationRef,
      extraActions
    }
  }),
  formName: 'assignRoomPurpose'
});

export const initialFilters = {
  search: '',
  roomTypes: [],
  rooms: [],
  floors: [],
  status: null,
  roomCleaningStatuses: [],
  startDate: null,
  endDate: null,
  dummy: null,
  archived: false,
  outOfOrderReasons: [],
  outOfOrderStartDate: null,
  outOfOrderEndDate: null,
  roomPurposeTypes: [],
  purposeEndDate: null,
  purposeStartDate: null
};

export const HOUSEKEEPING_STATUS_OPTIONS = {
  CLEAN: 'Clean',
  DIRTY: 'Dirty',
  INSPECTED: 'Inspected',
  OUT_OF_SERVICE: 'Out of service',
  OUT_OF_ORDER: 'Out of order'
};

export const HOUSEKEEPING_STATUS_TO_COLOR_MAPPER = {
  [HOUSEKEEPING_STATUS_OPTIONS.CLEAN]: 'var(--clear-blue)',
  [HOUSEKEEPING_STATUS_OPTIONS.DIRTY]: '#C5815C',
  [HOUSEKEEPING_STATUS_OPTIONS.INSPECTED]: '#FFBD5E',
  [HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_SERVICE]: 'var(--dark-red)',
  [HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_ORDER]: '#6E7B8C'
};

export const CLEANING_STATUSES = [
  {
    label: HOUSEKEEPING_STATUS_OPTIONS.CLEAN,
    value: HOUSEKEEPING_STATUS_OPTIONS.CLEAN
  },
  {
    label: HOUSEKEEPING_STATUS_OPTIONS.DIRTY,
    value: HOUSEKEEPING_STATUS_OPTIONS.DIRTY
  },
  {
    label: HOUSEKEEPING_STATUS_OPTIONS.INSPECTED,
    value: HOUSEKEEPING_STATUS_OPTIONS.INSPECTED
  },
  {
    label: HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_SERVICE,
    value: HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_SERVICE
  },
  {
    label: HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_ORDER,
    value: HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_ORDER
  }
];

export const CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN = [
  HOUSEKEEPING_STATUS_OPTIONS.DIRTY,
  HOUSEKEEPING_STATUS_OPTIONS.OUT_OF_SERVICE
];

export const ROOM_OCCUPANCY_STATUSES = [
  {
    label: 'Available',
    value: 'available'
  },
  {
    label: 'Out of order',
    value: 'offline_period'
  },
  {
    label: 'Reserved',
    value: 'reserved'
  },
  {
    label: 'Occupied',
    value: 'occupied'
  }
];

export const VIEW_CHOICES = {
  ALLOCATIONS: 'allocations',
  PURPOSES: 'purpose'
};

export const selectViewOptions = [
  {label: 'Room allocations', value: VIEW_CHOICES.ALLOCATIONS},
  {label: 'Room purpose', value: VIEW_CHOICES.PURPOSES}
];

export const buildOfflinePeriodReasonOption = reason => ({
  label: reason.title,
  value: reason.id
});

export const PURPOSES = {
  ['long_stay']: 'Long stay',
  ['short_stay']: 'Short stay',
  ['guest']: 'Guest',
  ['corporate']: 'Corporate',
  ['extended_stay']: 'Extended stay'
};

export const ROOM_PURPOSES_TYPES = [
  {value: 'long_stay', label: 'Long stay'},
  {value: 'short_stay', label: 'Short stay'},
  {value: 'extended_stay', label: 'Extended stay'},
  {value: 'corporate', label: 'Corporate'},
  {value: 'guest', label: 'Guest'}
];

const initialState = {};

const fetchReducer = createReducer(initialState, {
  ...createFetchReducers(fetchAllocationStatistics),
  ...fetchAllocationsReducer,
  ...fetchRoomsForRoomPurposesReducer,
  ...createFetchReducers(fetchOfflinePeriod),
  ...createFetchReducers(fetchOfflinePeriodReasons),
  ...createFetchReducers(fetchRoomHistory),
  ...createFetchReducers(fetchRoomsForRoomPurposesWithoutFilters)
});

const reducer = (state = initialState, action) => {
  const newState = fetchReducer(state, action);

  switch (action.type) {
    case SELECT_ROOM_CHECKBOX: {
      const payload = action.payload;
      const timelineData = newState.roomPurposesTimelineData;

      const groups = _.map(timelineData.groups, (element: {id: number}) => {
        if (element.id === payload.roomId) {
          return {...element, isSelected: payload.isSelected};
        }
        return element;
      });

      return {
        ...newState,
        roomPurposesTimelineData: {
          ...newState.roomPurposesTimelineData,
          groups
        }
      };
    }
    default:
      return newState;
  }
};

export default reducer;
export {default as allocationsSaga} from './saga';
